import { ScrollArea } from '../ui';
import { TASK_STATUS_BADGE, statusFill, statusInk } from '../../../lib/task-status';
import type { TokenBackedBadgeVariant } from '../../../lib/task-status';
import { ENTITY_TYPE, useGenericBi } from '../../../lib/use-generic-bi';
import { AGENT_TASK_BUCKET_BI_SLUG } from '../../../lib/agent-task-buckets';
import { TASKS_PAGE } from '../../../lib/tasks-bi';
import { useTranslation } from '../../../lib/i18n';
import type { TabKey } from './buckets';
import styles from './tasks.module.css';

// A tab's count pill is a COUNTER, not a <Badge> — it is round, it lives inside an
// interactive tab, and the DS has no component for it. It borrows the tint from the badge
// the same status wears (`statusFill`/`statusInk`), so the pill and the task's own status
// badge on the detail screen can never drift: Waiting = orange, In progress = blue,
// Done = green, Failed = red. `all` is the leading catch-all — a neutral pill, no status tint.
//
// `all` is retained on purpose even though the strip no longer renders it — the tab is HIDDEN, not
// deleted (see `taskTabKeys` / `TasksBody`), and this entry is what it comes back with.
const TAB_META: Record<TabKey, { labelKey: string; variant?: TokenBackedBadgeVariant }> = {
  all: { labelKey: 'workspace.tasks.all' },
  waiting: { labelKey: 'tasks.status.actionNeeded', variant: TASK_STATUS_BADGE.ACTION_NEEDED.variant },
  // The in-progress TAB is labelled "Agent Tasks", not "In progress": the tab names the work, while the
  // per-task STATUS badge keeps `tasks.status.inProgress`. Two different jobs — a badge that said
  // "Agent Tasks" would state no status at all. Mirrored on iOS/Android (`AgentTasksScreen.tabTitle`,
  // `AgentTaskBucket.title`), all three reading the SAME key so the copy cannot drift.
  //
  // ⚠️ `agents.tasksAtWork` was RE-ADDED to web's 37 bundles for this. #9386 deleted it here (the agents
  // island moved to its own `agentsIsland.*` vocabulary and left the key unused) while both native
  // catalogs kept it — so this is the shared key restored, not a new one, and its values are the
  // originals recovered from git rather than fresh translations.
  inProgress: { labelKey: 'agents.tasksAtWork', variant: TASK_STATUS_BADGE.IN_PROGRESS.variant },
  done: { labelKey: 'tasks.status.done', variant: TASK_STATUS_BADGE.COMPLETED.variant },
  failed: { labelKey: 'tasks.status.failed', variant: TASK_STATUS_BADGE.FAILED.variant },
};

// Renders exactly the tabs it is given — the caller decides which are visible (`visibleTaskTabs`, plus the
// leading `all`). A count badge is only drawn for a non-zero tab, so an empty tab reads as a plain label
// rather than a "0"; the active status tab tints its pill, `all` stays neutral.
//
// On mobile the tabs don't fit, so the strip scrolls sideways (native does the same). All of the scroll
// behaviour is CSS on `.tabs` (see tasks.module.css): the cross-axis is clipped so it can't be dragged
// vertically, `scroll-behavior: smooth` keeps a tap/keyboard reveal from snapping, and
// `overscroll-behavior: contain` stops a swipe chaining to the page / browser back-nav.
export function TaskTabs({
  tabs,
  counts,
  active,
  onSelect,
}: {
  tabs: TabKey[];
  counts: Record<TabKey, number>;
  active: TabKey;
  onSelect: (tab: TabKey) => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  return (
    <ScrollArea axis="horizontal" bar="hidden" className={styles.tabs} role="tablist">
      {tabs.map((tab) => {
        const meta = TAB_META[tab];
        const isActive = tab === active;
        const tinted = isActive && meta.variant;
        return (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => {
              // The tab itself is the entity acted on, and the bucket's size is the context that
              // makes the click readable ("switched to a Waiting tab holding 12"). `entity.id` is the
              // shared slug, not the TS key — see `AGENT_TASK_BUCKET_BI_SLUG`; the leading `all` catch-all
              // has no bucket slug, so it reports as `all`.
              logPageClickBi(
                { ...TASKS_PAGE, button_name: 'switch_tab' },
                {
                  componentName: `switch_tab_${tab}`,
                  entity: { id: tab === 'all' ? 'all' : AGENT_TASK_BUCKET_BI_SLUG[tab], type: ENTITY_TYPE.tab },
                  additionalFields: { task_count: String(counts[tab] ?? 0) },
                },
              );
              onSelect(tab);
            }}
            className={`${styles.tab} ${isActive ? styles.tabActive : ''}`}
          >
            {t(meta.labelKey)}
            {counts[tab] > 0 && (
              <span
                className={styles.tabBadge}
                style={tinted ? { background: statusFill(meta.variant!), color: statusInk(meta.variant!) } : undefined}
              >
                {counts[tab]}
              </span>
            )}
          </button>
        );
      })}
    </ScrollArea>
  );
}
